import { Ability, AbilityBuilder, AbilityClass } from '@casl/ability'
import {
  ACTIONS,
  SUBJECTS,
  SubjectsUnion,
  Actions,
  AppAbility,
  Conditions
} from 'features/Permission/constants'
import { IUser } from 'redux/store/user/types'

import defineRFPAbilities from 'features/Permission/abilities/rfpAbilities'
import { VendorDetails } from 'features/MyCommunity/Vendors/VendorCreation/types'
import {
  updateGpoAbilities,
  defineGpoProfileAbilities
} from './abilities/gpo/gpoAbilities'
import {
  defineCommonsProfileAbilities,
  updateCommonsAbilities
} from './abilities/commons/commonsAbilities'
import {
  defineVendorProfileAbilities,
  updateVendorAbilities
} from './abilities/vendor/vendorAbilities'
import { defineCommunityProfileAbilities } from './abilities/community/profileAbilities'
import { defineCommunityAbilities } from './abilities/community/communityAbilities'
import { updateCommunityAbilities } from './abilities/community/communityUpdatePermissions'

export const ability = new Ability<[Actions, SubjectsUnion], Conditions>()
export const AppAbilityClass = Ability as AbilityClass<AppAbility>

const defineAbilityFor = (role: string, user: IUser, profileId?: string) => {
  const builder = new AbilityBuilder(AppAbilityClass)
  const { can, rules } = builder

  can(ACTIONS.VIEW, SUBJECTS.MY_COMMUNITY)

  if (user.uuid === profileId) {
    can(ACTIONS.UPLOAD, SUBJECTS.AVATAR)
    can(ACTIONS.EDIT, SUBJECTS.FORM_SAVINGS_GOAL)
  }

  if (user.role.includes('commons')) {
    defineCommonsProfileAbilities(builder, role, user, profileId)
  }
  if (user.role.includes('vendor')) {
    defineVendorProfileAbilities(builder, role, user, profileId)
  }
  if (user.role.includes('community')) {
    defineCommunityAbilities(builder, user)
    defineCommunityProfileAbilities(builder, role, user, profileId)
  }
  if (user.role.includes('gpo')) {
    defineGpoProfileAbilities(builder)
  }
  return new Ability(rules)
}

export const updateAbility = (
  passAbility: Ability<[Actions, SubjectsUnion], Conditions>,
  user: IUser,
  vendor?: VendorDetails | null
) => {
  const ability = new AbilityBuilder(AppAbilityClass)

  const isOwnerCondition = {
    'creator.uuid': user.uuid
  }

  const isNotOwnerCondition = {
    'creator.uuid': {
      $ne: user.uuid
    }
  }

  const isHSOwnerCondition = {
    uuid: user.health_system
  }
  const isHospitalOwnerCondition = {
    uuid: user.hospital
  }

  const isEventParticipant = {
    participants: {
      $elemMatch: { uuid: user.uuid }
    }
  }

  const ctx = {
    user,
    vendor,
    isHSOwnerCondition,
    isHospitalOwnerCondition
  }

  const { can, rules } = ability

  can(ACTIONS.VIEW, SUBJECTS.PROFILE)

  can(ACTIONS.VIEW, SUBJECTS.NOTIFICATIONS)
  can(ACTIONS.VIEW, SUBJECTS.SETTINGS)
  can(ACTIONS.VIEW, SUBJECTS.LOG_OUT)

  defineRFPAbilities(ability, user)
  can(ACTIONS.CRUD, SUBJECTS.CALENDAR_EVENT, isOwnerCondition)
  can(ACTIONS.CRUD, SUBJECTS.CALENDAR_EVENT_RESPONSE, {
    ...isEventParticipant,
    ...isNotOwnerCondition
  })

  if (user.role.includes('community')) {
    updateCommunityAbilities(ability, ctx)
  }

  if (user.role.includes('commons')) {
    updateCommonsAbilities(ability, ctx)
  }

  if (user.role.includes('vendor')) {
    updateVendorAbilities(ability, ctx)
  }

  if (user.role.includes('gpo')) {
    updateGpoAbilities(ability, ctx)
  }

  passAbility.update(rules)
}

export default defineAbilityFor
